import React, { useState } from 'react';
import { Project } from '../../api/supabaseEntities.js';
import { User } from '../../api/supabaseEntities.js';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "../ui/card.jsx";
import { Button } from "../ui/button.jsx";
import { Badge } from "../ui/badge.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar.jsx";
import { ScrollArea } from "../ui/scroll-area.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.jsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog.jsx";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "../ui/dialog.jsx";
import { Input } from "../ui/input.jsx";
import { Separator } from "../ui/separator.jsx";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipTrigger 
} from "../ui/tooltip.jsx";
import {
  UserPlus,
  UserX,
  Users,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Mail,
  Phone,
  Building2,
  Clock,
  Calendar,
  Star,
  Award,
  Trash2,
  Plus,
  Search,
  UserCog,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ProjectTeam({ project, manager, teamMembers, internalUsers = [], onTeamUpdate }) {
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [isChangingManager, setIsChangingManager] = useState(false);
  const [newManager, setNewManager] = useState(null);

  const handleAddMember = async (user) => {
    try {
      const updatedTeam = [...(project.team || []), user.email];
      await Project.update(project.id, { team: updatedTeam });
      onTeamUpdate && onTeamUpdate();
      setIsAddingMember(false);
      setSelectedMember(null);
    } catch (error) {
      console.error('Error adding team member:', error);
      alert('Erro ao adicionar membro à equipe');
    }
  };

  const handleRemoveMember = async (email) => {
    try {
      const updatedTeam = (project.team || []).filter(member => member !== email);
      await Project.update(project.id, { team: updatedTeam });
      onTeamUpdate && onTeamUpdate();
      setMemberToRemove(null);
    } catch (error) {
      console.error('Error removing team member:', error);
      alert('Erro ao remover membro da equipe');
    }
  };

  const handleChangeManager = async () => {
    if (!newManager) return;
    
    try {
      await Project.update(project.id, { manager: newManager.email });
      onTeamUpdate && onTeamUpdate();
      setIsChangingManager(false);
      setNewManager(null);
    } catch (error) {
      console.error('Error changing project manager:', error);
      alert('Erro ao alterar gestor do projeto');
    }
  };

  // Filtra usuários disponíveis para adicionar (que não estão na equipe ainda)
  const availableUsers = internalUsers.filter(user => 
    !project.team?.includes(user.email) && 
    user.email !== project.manager &&
    (searchTerm === '' || 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Gestor do Projeto */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Gestor do Projeto
            </CardTitle>
            <Dialog open={isChangingManager} onOpenChange={setIsChangingManager}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <UserCog className="h-4 w-4 mr-2" />
                  Alterar Gestor
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Alterar Gestor do Projeto</DialogTitle>
                  <DialogDescription>
                    Selecione o novo gestor para o projeto. Esta ação transferirá todas as responsabilidades de gestão.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Select
                      value={newManager?.email}
                      onValueChange={(value) => {
                        const user = internalUsers.find(u => u.email === value);
                        setNewManager(user);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o novo gestor" />
                      </SelectTrigger>
                      <SelectContent>
                        {internalUsers
                          .filter(user => user.email !== project.manager)
                          .map((user) => (
                            <SelectItem key={user.email} value={user.email}>
                              <div className="flex items-center gap-2">
                                <Avatar className="h-6 w-6">
                                  <AvatarImage src={user.avatar_url} />
                                  <AvatarFallback>
                                    {user.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <span>{user.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsChangingManager(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleChangeManager} disabled={!newManager}>
                    Confirmar Alteração
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {manager ? (
            <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
              <Avatar className="h-12 w-12">
                <AvatarImage src={manager.avatar_url} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {manager.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">{manager.name}</h3>
                  <Badge variant="secondary" className="font-normal">
                    Gestor
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground space-y-1 mt-1">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {manager.email}
                  </div>
                  {manager.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {manager.phone}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-4">
              <ShieldAlert className="h-8 w-8 mx-auto mb-2" />
              Nenhum gestor definido
            </div>
          )}
        </CardContent>
      </Card>

      {/* Membros da Equipe */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Membros da Equipe
              <Badge variant="secondary" className="ml-2">
                {teamMembers?.length || 0}
              </Badge>
            </CardTitle>
            <Dialog open={isAddingMember} onOpenChange={setIsAddingMember}>
              <DialogTrigger asChild>
                <Button>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Adicionar Membro
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[525px]">
                <DialogHeader>
                  <DialogTitle>Adicionar Membro à Equipe</DialogTitle>
                  <DialogDescription>
                    Selecione um usuário para adicionar à equipe do projeto
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar por nome ou email..."
                      className="pl-8"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <ScrollArea className="h-[300px] pr-4">
                    <div className="space-y-2">
                      {availableUsers.map((user) => (
                        <motion.div
                          key={user.email}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                        >
                          <div 
                            className={`
                              flex items-center justify-between p-3 rounded-lg cursor-pointer
                              ${selectedMember?.email === user.email ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/50'}
                              transition-colors
                            `}
                            onClick={() => setSelectedMember(user)}
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={user.avatar_url} />
                                <AvatarFallback>
                                  {user.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">{user.name}</div>
                                <div className="text-sm text-muted-foreground">{user.email}</div>
                              </div>
                            </div>
                            {selectedMember?.email === user.email && (
                              <CheckCircle2 className="h-5 w-5 text-primary" />
                            )}
                          </div>
                        </motion.div>
                      ))}
                      {availableUsers.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          <UserX className="h-8 w-8 mx-auto mb-2" />
                          <p>Nenhum usuário disponível encontrado</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setIsAddingMember(false);
                    setSelectedMember(null);
                    setSearchTerm('');
                  }}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={() => handleAddMember(selectedMember)}
                    disabled={!selectedMember}
                  >
                    Adicionar à Equipe
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {teamMembers && teamMembers.length > 0 ? (
                teamMembers.map((member) => (
                  <motion.div
                    key={member.email}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={member.avatar_url} />
                              <AvatarFallback>
                                {member.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium">{member.name}</div>
                              <div className="text-sm text-muted-foreground">{member.email}</div>
                              {member.role && (
                                <Badge variant="secondary" className="mt-1">
                                  {member.role}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remover Membro da Equipe</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja remover {member.name} da equipe do projeto?
                                  Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleRemoveMember(member.email)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Remover
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2" />
                  <p>Nenhum membro na equipe</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}